import { toCalendarDateTime } from "@internationalized/date";
import { useMutation } from "@tanstack/react-query";
import {
  CheckmarkFill18Icon,
  CloseFill24Icon,
  DropdownDownOutline18Icon,
  DropdownUpOutline18Icon,
  TimeOutline18Icon,
} from "@vygruppen/spor-icon-react";
import { getBackendUrl } from "api/common";
import { mutationFnPOST } from "api/tanStackQuery/helpers";
import { queryClient } from "api/tanStackQuery/queryClient";
import { format } from "date-fns";
import { AffectedTrainBadge } from "features/CenterContent/RoleContent/ProductionDeviation/components/AffectedTrainBadge";
import { EditProductionDeviationButton } from "features/CenterContent/RoleContent/ProductionDeviation/components/EditProductionDeviationButton";
import { ProductionDeviationResponse } from "features/CenterContent/RoleContent/ProductionDeviation/types/responseSchema";
import { Badges } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/UpcomingEventsModal/Badges";
import { ReasonTypeDisplay } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/UpcomingEventsModal/components/ReasonTypeDisplay";
import { TrainSerieDisplay } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/UpcomingEventsModal/components/TrainSerieDisplay";
import { TrainTypeDisplay } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/UpcomingEventsModal/components/TrainTypeDisplay";
import {
  allProductionDeviationsQueryKey,
  productionDeviationQueryKey,
} from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/UpcomingEventsModal/useProductionDeviation";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { FailureMessageWithRetryButton } from "shared/components/feedback/FailureMessageWithRetryButton";
import { SegmentedPanel } from "shared/components/SegmentedPanel";
import {
  Box,
  DropsButton,
  HStack,
  Text as SporText,
  Stack,
  tokens,
} from "shared/components/Spor";
import { Text } from "shared/components/typography/TitleStyles";
import { css, useTheme } from "styled-components";

type ConfirmAction = "close" | "depublish";

type ProductionDeviationElementProps = {
  productionDeviation: ProductionDeviationResponse;
};

export const ProductionDeviationElement: FC<
  ProductionDeviationElementProps
> = ({ productionDeviation }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [showAffectedTrains, setShowAffectedTrains] = useState(false);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(
    null,
  );
  const {
    reference,
    eventType,
    reasonType,
    message,
    updatedAt,
    fromDate,
    toDate,
    countryCode,
    eventCount,
    trainsByDate,
  } = productionDeviation;
  const durations = {
    fromTime: toCalendarDateTime(fromDate),
    toTime: toCalendarDateTime(toDate),
  };
  const closeMutation = useMutation({
    mutationFn: () =>
      mutationFnPOST(
        `${getBackendUrl()}/production-deviation/${reference}/close`,
      ),
    onSuccess: () => {
      setConfirmAction(null);
      void queryClient.invalidateQueries({
        queryKey: productionDeviationQueryKey(countryCode),
      });
      void queryClient.invalidateQueries({
        queryKey: allProductionDeviationsQueryKey,
      });
    },
  });
  const depublishMutation = useMutation({
    mutationFn: () =>
      mutationFnPOST(
        `${getBackendUrl()}/production-deviation/${reference}/depublish`,
      ),
    onSuccess: () => {
      setConfirmAction(null);
      void queryClient.invalidateQueries({
        queryKey: productionDeviationQueryKey(countryCode),
      });
      void queryClient.invalidateQueries({
        queryKey: allProductionDeviationsQueryKey,
      });
    },
  });

  const confirmButtons = (onConfirm: () => void, isPending: boolean) => (
    <HStack w="124px" h="30px" gap="4px">
      <DropsButton
        variant="primary"
        semantics="alarm"
        size="xs"
        w="30px"
        h="30px"
        minW="auto"
        p="0"
        onClick={() => setConfirmAction(null)}
        aria-label={t("Avbryt")}
      >
        <Box color="white">
          <CloseFill24Icon />
        </Box>
      </DropsButton>
      <DropsButton
        variant="primary"
        semantics="success"
        size="xs"
        onClick={onConfirm}
        loading={isPending}
        leftIcon={<CheckmarkFill18Icon />}
      >
        {t("Bekreft")}
      </DropsButton>
    </HStack>
  );

  const renderDepublish = () => {
    if (depublishMutation.isError)
      return (
        <FailureMessageWithRetryButton
          customMessage={t("Kunne ikke depublisere hendelsen")}
          retryFunction={() => depublishMutation.mutate()}
        />
      );
    if (confirmAction === "depublish")
      return confirmButtons(
        () => depublishMutation.mutate(),
        depublishMutation.isPending,
      );
    return (
      <DropsButton
        size="xs"
        variant="tertiary"
        onClick={() => setConfirmAction("depublish")}
      >
        {t("Depubliser")}
      </DropsButton>
    );
  };

  const renderClose = () => {
    if (closeMutation.isError)
      return (
        <FailureMessageWithRetryButton
          customMessage={t("Kunne ikke friskmelde hendelsen")}
          retryFunction={() => closeMutation.mutate()}
        />
      );
    if (confirmAction === "close")
      return confirmButtons(
        () => closeMutation.mutate(),
        closeMutation.isPending,
      );
    return (
      <DropsButton
        size="xs"
        variant="tertiary"
        onClick={() => setConfirmAction("close")}
      >
        {t("Friskmeld")}
      </DropsButton>
    );
  };

  return (
    <Stack key={reference}>
      <SegmentedPanel
        panelStyle={css`
          background: ${theme.colorBackgroundTertiary};
          border-radius: 12px;
          padding: 18px;
        `}
        dashedLineVariant="tertiary"
      >
        <Stack gap="0">
          <HStack w="100%" justifyContent="space-between">
            <HStack>
              <TrainSerieDisplay
                trainIds={trainsByDate.flatMap(({ trainIds }) => trainIds)}
              />
            </HStack>
            <HStack>
              <EditProductionDeviationButton uuid={reference} />
              {renderDepublish()}
              {renderClose()}
            </HStack>
          </HStack>
          <Stack gap="0" mt={tokens.default.size.spacing[1.5]}>
            <TrainTypeDisplay eventType={eventType} />
          </Stack>
          <HStack mt={tokens.default.size.spacing[2]}>
            <ReasonTypeDisplay reasonType={reasonType} />
          </HStack>
          <Stack gap="0" mt={tokens.default.size.spacing[1]}>
            <Badges displayDuration={durations} variant="sm" />
          </Stack>
          {message && (
            <Text
              as="span"
              style={{ marginTop: tokens.default.size.spacing[2] }}
            >
              {message}
            </Text>
          )}
          <Text
            as="span"
            secondary
            style={{ marginTop: tokens.default.size.spacing[2] }}
          >
            {t("Sist oppdatert:")}{" "}
            {format(updatedAt.toDate(), "dd.MM.yyyy 'kl.' HH:mm")}
          </Text>

          {showAffectedTrains &&
            trainsByDate.map(({ date, trainIds: trainIdsForDate }, index) => (
              <Stack
                key={date.toISOString()}
                gap={tokens.default.size.spacing[1]}
                mt={
                  index === 0
                    ? tokens.default.size.spacing[3]
                    : tokens.default.size.spacing[2]
                }
              >
                <HStack gap={tokens.default.size.spacing[1]}>
                  <TimeOutline18Icon />
                  <SporText
                    variant="sm"
                    fontWeight={700}
                    color={theme.colorTextMain}
                  >
                    {format(date, "dd.MM.yy")}
                  </SporText>
                </HStack>
                <HStack gap={tokens.default.size.spacing[1]} wrap="wrap">
                  {[...trainIdsForDate]
                    .sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
                    .map((trainId) => (
                      <AffectedTrainBadge key={trainId} trainId={trainId} />
                    ))}
                </HStack>
              </Stack>
            ))}

          <HStack justifyContent="center" mt={tokens.default.size.spacing[2]}>
            <DropsButton
              variant="ghost"
              size="xs"
              onClick={() => setShowAffectedTrains((prev) => !prev)}
              rightIcon={
                showAffectedTrains ? (
                  <DropdownUpOutline18Icon />
                ) : (
                  <DropdownDownOutline18Icon />
                )
              }
            >
              {showAffectedTrains
                ? t("Skjul berørte tog")
                : t("Vis berørte tog")}{" "}
              ({eventCount})
            </DropsButton>
          </HStack>
        </Stack>
      </SegmentedPanel>
    </Stack>
  );
};
